import template from 'url-template';

import { get, isEmpty, isNil } from 'lodash';

import {
    FETCH_AGENT_LOGS,
    FETCH_AGENT_LOG_ENTRIES,
    RESET_AGENT_LOG_FILTERS,
    SET_AGENT_LOG_END_TIMESTAMP,
    SET_AGENT_LOG_META_DATA,
    SET_AGENT_LOG_SEARCH_VALUE,
    SET_AGENT_LOG_START_TIMESTAMP,
    SET_AGENT_LOG,
    SET_PROFILE_OBJECT_PROCESS_DATA,
    SET_PROFILE_OBJECT_PROCESS_IDS,
} from '../actions/actionTypes';

import { handleNetworkError, enqueueSnackbar } from './applicationMessageActions';
import { setBobLoading } from './bobActions';

import awsHeader from '../helpers/awsAuth';
import k2aBaseApi from '../apis/key2act';

import logDescriptionTable from '../helpers/logNamesAndDescriptions';

const DevicesUrl = template.parse('/basagents/{agentId}/basdevices{?configurationId,searchFilter,limit,page,sort}');
const DeviceObjectsUrl = template.parse('/basdevices/{basDeviceId}/basdevice-objects{?page,limit,sort}');
const BulkProfileObjectUrls = template.parse('/basprofiles/{profileId}/bulk-basprofile-objects');
const LogsUrl = template.parse('/basagents/logs{?CompanyId,SearchObjectType,SerialNumber,LogGroup,Limit,StartDateTime,EndDateTime,NextToken,FilterPattern}');

export const fetchLogs = options => async dispatch => {
    const header = await awsHeader();

    const {
        companyId,
        serialNumber
    } = options;

    try {
        const response = await k2aBaseApi.get(
            LogsUrl.expand({
                CompanyId:        companyId,
                SearchObjectType: 'LogGroups',
                SerialNumber:     serialNumber
            }),
            header
        );

        const rawPayload = get(response, 'data.data', []);

        const payload = rawPayload.map(log => {
            return {
                machineName: log,
                ...logDescriptionTable(log)
            };
        });

        dispatch({
            type: FETCH_AGENT_LOGS,
            payload
        });
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const fetchDevicesAndObjects = (agentId, configurationId, options = {}) => async (getState, dispatch) => {
    const header = await awsHeader();

    dispatch(setBobLoading('FetchDevicesAndObjects'));
    try {
        const response = await k2aBaseApi.get(
            DevicesUrl.expand({
                agentId,
                configurationId,
                ...options
            }),
            header
        );

        const devices = get(response, 'data.data', []);

        let deviceObjects = [];

        for (const device of devices) {
            const basDeviceId = device.id;

            const response = await k2aBaseApi.get(
                DeviceObjectsUrl.expand({
                    basDeviceId,
                    ...options
                }),
                header
            );
            const objects = get(response, 'data.data', []);
            objects.forEach(item => {
                item.deviceName = device.name;
                item.deviceId = device.id;
            });
            deviceObjects = [...deviceObjects, ...objects];
        }

        dispatch(setBobLoading('FetchDevicesAndObjects'));
        return deviceObjects;
    } catch (error) {
        dispatch(handleNetworkError(error));
        dispatch(setBobLoading('FetchDevicesAndObjects'));
        return [];
    }
};

export const fetchLogEntries = (options = {}) => async dispatch => {
    if (!options || isEmpty(options)) {
        dispatch({
            type:    FETCH_AGENT_LOG_ENTRIES,
            payload: null
        });

        return;
    }

    const header = await awsHeader();

    const {
        companyId,
        serialNumber,
        logGroup,
        startDateTime,
        endDateTime,
        filterPattern
    } = options;

    try {
        const response = await k2aBaseApi.get(
            LogsUrl.expand({
                CompanyId:        companyId,
                SearchObjectType: 'AgentLogs',
                SerialNumber:     serialNumber,
                LogGroup:         logGroup,
                Limit:            '2000',
                StartDateTime:    startDateTime,
                EndDateTime:      endDateTime,
                FilterPattern:    filterPattern
            }),
            header
        );

        // assign data to payload or if null (sometimes server gives us a null
        // instead of empty array) assign it an empty array.
        const payload = get(response, 'data.data', []) || [];

        const recordsMatched = get(response, 'data.recordsMatched', 0);
        const returnRecordCount = get(response, 'data.returnRecordCount', 0);
        const status = get(response, 'data.status');

        if (status === 'Complete') {
            if (payload.length) {
                payload.forEach((item, index) => item.rowCount = index + 1);
            }

            dispatch({
                type: FETCH_AGENT_LOG_ENTRIES,
                payload
            });

            dispatch({
                type:    SET_AGENT_LOG_META_DATA,
                payload: {
                    recordsMatched,
                    returnRecordCount
                }
            });
        } else {
            dispatch(enqueueSnackbar('Request timed out. Hit Reset to try again.', 'warning'));
        }
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const processBulkProfileObject = (summaryData, redirect, setIsSubmitting, setRequestErrors) => async (dispatch, getState) => {
    let errors = [];
    const resultErrors = summaryData.filter(item => item.status === 'Error');

    resultErrors.forEach(item => {
        errors.push({
            item,
            status: 'rejected',
            error:  item.messages
        });
    });

    const resultSuccesses = summaryData.filter(item => item.status === 'Success');

    setIsSubmitting(false);
    setRequestErrors(errors);

    if (!resultErrors.length) {
        dispatch(enqueueSnackbar(`${resultSuccesses.length} Profile object(s) created successfully`, 'success'));
        redirect();
    }
};

export const createBulkProfileObject = (profileId, profileObjectData, redirect, setIsSubmitting, setRequestErrors, setCompletedObjects, setPendingObjects) => async (dispatch, getState) => {
    const header = await awsHeader();
    let profileObjects = {
        profileObjects: [...profileObjectData]
    };
    //bulk endpoint body

    let ids = [];
    let summaryData = [];

    try {
        setPendingObjects(profileObjects.profileObjects.length);
        const response = await k2aBaseApi.post(
            BulkProfileObjectUrls.expand({ profileId }),
            profileObjects,
            header
        );

        const payload = get(response, 'data', []);

        payload.forEach(item => {
            ids.push(item.processId);
        });

        dispatch(setProfileObjectProcessIds(ids));

        const getProcess = async ids => {
            if (isEmpty(ids)) {
                return;
            }

            const selectedId = ids[0];
            const resolvedData = getState().iotHub.profileObjectProcessData;
            const selectedData = resolvedData.find(item => item.processId === selectedId);

            const recurseProcesses = async () => {
                return await getProcess(ids);
            };

            if (isNil(selectedData)) {
                setTimeout(recurseProcesses, 1000);
            } else {
                const modifiedIds = ids.filter(id => id !== selectedId);
                summaryData = [...summaryData, ...selectedData.data];
                const recurseModifiedIds = async () => {
                    return await getProcess(modifiedIds);
                };
                setCompletedObjects(summaryData.length);
                if (modifiedIds.length) {
                    setTimeout(recurseModifiedIds, 1000);
                } else {
                    dispatch(processBulkProfileObject(summaryData, redirect, setIsSubmitting, setRequestErrors));
                    return;
                }

            }
        };

        await getProcess(ids);
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const setProfileObjectProcessData = payload => async dispatch => {
    dispatch({
        type: SET_PROFILE_OBJECT_PROCESS_DATA,
        payload
    });
};

export const setProfileObjectProcessIds = payload => async dispatch => {
    dispatch({
        type: SET_PROFILE_OBJECT_PROCESS_IDS,
        payload
    });
};

export const resetAgentLogFilters = () => async dispatch => {
    dispatch({
        type: RESET_AGENT_LOG_FILTERS
    });
};

export const resetCounters = () => async dispatch => {
    dispatch({
        type:    SET_AGENT_LOG_META_DATA,
        payload: {
            recordsMatched:    0,
            returnRecordCount: 0
        }
    });
};

export const setLogEndTimeStamp = timestamp => async dispatch => {
    dispatch({
        type:    SET_AGENT_LOG_END_TIMESTAMP,
        payload: timestamp
    });
};

export const setLogData = payload => ({
    type: SET_AGENT_LOG,
    payload
});

export const setLogSearchValue = searchValue => async dispatch => {
    dispatch({
        type:    SET_AGENT_LOG_SEARCH_VALUE,
        payload: searchValue
    });
};

export const setLogStartTimeStamp = timestamp => async dispatch => {
    dispatch({
        type:    SET_AGENT_LOG_START_TIMESTAMP,
        payload: timestamp
    });
};
